import { formatMoney } from '@theme/money-formatting';

/* Trinsic PDP. ATC/cart is owned by native <product-form-component>; this script
   only updates on-page UI and writes the hidden id + selling_plan inputs.
   Availability is server-rendered — JS only re-derives it on a real variant switch,
   never at init.

   Money: every FIXED price on this page is server-rendered (Liquid | money). The one
   exception is the quantity total on the ATC label and the sticky bar — the quantity
   is unbounded, so those cannot be pre-rendered. They go through Horizon's own
   money-formatting module fed with the shop's real money_format + currency (emitted
   from Liquid on the section root), which replicates the | money filter exactly. No
   Intl, no locale guessing, no hand-rolled formatter. */

(function () {
  'use strict';

  function initSection(root) {
    if (!root || root.dataset.tmpInit === 'true') return;
    root.dataset.tmpInit = 'true';

    /* --- Variant data (multi-variant switching only) --- */
    var variants = [];
    var dataEl = root.querySelector('[data-tmp-variant-json]');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; }
    }

    /* Per-variant money, server-rendered (Liquid | money) — keyed by variant id. */
    var variantPrices = {};
    var priceMapEl = root.querySelector('[data-tmp-variant-prices]');
    if (priceMapEl) {
      try { variantPrices = JSON.parse(priceMapEl.textContent) || {}; } catch (e) { variantPrices = {}; }
    }

    /* --- Image gallery ---
       Every image is its own slide in the DOM. Desktop paints only `.is-active`
       and the arrows/thumbs pick it; at ≤749px the CSS turns the track into a
       scroll-snap carousel, so a finger swipe becomes the source of truth and the
       settled scroll position is read back into the active index. */
    var track = root.querySelector('[data-tmp-track]');
    var slides = root.querySelectorAll('[data-tmp-slide]');
    var thumbs = root.querySelectorAll('[data-tmp-thumb]');
    var prevBtn = root.querySelector('[data-tmp-prev]');
    var nextBtn = root.querySelector('[data-tmp-next]');
    var thumbsContainer = root.querySelector('[data-tmp-thumbs]');
    var currentIndex = 0;
    var totalImages = slides.length || 1;

    /* Swipe mode is read off the DOM rather than guessed from a breakpoint: only
       the mobile rules let every slide paint, so only there does the track overflow. */
    function isSwipeMode() {
      return !!track && track.scrollWidth > track.clientWidth + 1;
    }

    /* Everything that follows from "slide N is current" EXCEPT moving the track —
       a manual swipe reuses this with scrollTrack=false so nothing fights the finger. */
    function applyIndex(index, scrollTrack) {
      if (index < 0 || index >= totalImages) return;
      currentIndex = index;

      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
      });

      var thumb = thumbs[index];
      if (thumbsContainer && thumb) {
        var scrollLeft =
          thumb.offsetLeft -
          thumbsContainer.offsetLeft -
          thumbsContainer.clientWidth / 2 +
          thumb.offsetWidth / 2;
        thumbsContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }

      if (scrollTrack && isSwipeMode()) {
        track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      }
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        applyIndex(parseInt(thumb.getAttribute('data-index'), 10) || 0, true);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        applyIndex((currentIndex - 1 + totalImages) % totalImages, true);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        applyIndex((currentIndex + 1) % totalImages, true);
      });
    }

    /* Swipe → active index. Only read once the track has SETTLED (scrollend where
       supported, debounce elsewhere): mid-flight frames — including those from our
       own smooth scrollTo — would otherwise strobe the thumbnail state. */
    if (track) {
      var scrollSyncTimer = null;

      var syncFromScroll = function () {
        if (!isSwipeMode()) return;
        var width = track.clientWidth;
        if (!width) return;
        var index = Math.round(track.scrollLeft / width);
        if (index < 0) index = 0;
        if (index > totalImages - 1) index = totalImages - 1;
        if (index !== currentIndex) applyIndex(index, false);
      };

      if ('onscrollend' in window) {
        track.addEventListener('scrollend', syncFromScroll);
      } else {
        track.addEventListener(
          'scroll',
          function () {
            if (scrollSyncTimer) clearTimeout(scrollSyncTimer);
            scrollSyncTimer = setTimeout(syncFromScroll, 120);
          },
          { passive: true }
        );
      }
    }

    /* Swap the gallery to the selected variant's photo (multi-variant only).
       Match slides by media id; the track is scrolled too, so the swap is visible
       in swipe mode instead of silently changing an off-screen slide. */
    function updateGalleryImage(variant) {
      var mediaId = variant && variant.featured_image && variant.featured_image.id;
      if (!mediaId || !slides.length) return;
      for (var i = 0; i < slides.length; i++) {
        if (String(slides[i].getAttribute('data-media-id')) === String(mediaId)) {
          applyIndex(i, true);
          return;
        }
      }
    }

    /* --- Buy box: purchase-option + variant wiring --- */
    var variantIdInput = root.querySelector('input[name="id"]');
    var sellingPlanInput = root.querySelector('[data-tmp-selling-plan-input]');
    var optionInputs = root.querySelectorAll('[data-tmp-option-input]');

    var priceCompareEl = root.querySelector('[data-tmp-price-compare]');
    var priceSaleEl = root.querySelector('[data-tmp-price-sale]');
    var priceBadgeEl = root.querySelector('[data-tmp-price-badge]');

    var purchaseOptions = root.querySelector('[data-tmp-purchase-options]');
    var sellingPlanSelect = root.querySelector('[data-tmp-selling-plan]');
    var subPriceEl = root.querySelector('[data-tmp-sub-price]');
    var subBadgeEl = root.querySelector('[data-tmp-sub-badge]');
    var oneTimePriceEl = root.querySelector('[data-tmp-onetime-price]');

    var atcButton = root.querySelector('.trinsic-pdp-atc');
    var atcText = root.querySelector('.trinsic-pdp-atc__text');

    /* Quantity stepper is Horizon's native <quantity-selector-component>; its
       input is the form's only name="quantity" field. We read it, never write
       the cart from it. */
    var qtyInput = root.querySelector('quantity-selector-component input[name="quantity"]');
    var nudge = root.querySelector('[data-tmp-nudge]');
    var nudgeLines = root.querySelectorAll('[data-tmp-nudge-tier]');
    var buyRow = root.querySelector('[data-tmp-buy-row]');

    /* The sticky bar is a sibling of the section root, so scope the lookup to the
       shopify-section wrapper rather than the root itself. */
    var sectionScope = root.closest('.shopify-section') || document;
    var stickyBar = root.dataset.section
      ? sectionScope.querySelector('[data-tmp-sticky][data-section="' + root.dataset.section + '"]')
      : sectionScope.querySelector('[data-tmp-sticky]');
    var stickyPriceEl = stickyBar ? stickyBar.querySelector('[data-tmp-sticky-price]') : null;
    var stickyAtcButton = stickyBar ? stickyBar.querySelector('[data-tmp-sticky-atc]') : null;

    var hasSellingPlans = !!purchaseOptions;

    /* Volume tiers, from the editor. One-time purchases only — see tierPercent(). */
    var tier2Percent = parseFloat(root.getAttribute('data-tmp-tier2')) || 0;
    var tier3Percent = parseFloat(root.getAttribute('data-tmp-tier3')) || 0;
    var moneyFormat = root.getAttribute('data-tmp-money-format') || '{{amount}}';
    var currencyCode = root.getAttribute('data-tmp-currency') || '';

    var currentVariantId = variantIdInput ? variantIdInput.value : '';
    var purchaseType = 'onetime';
    if (purchaseOptions) {
      var preselected = purchaseOptions.querySelector('.is-selected[data-tmp-option]');
      purchaseType = preselected ? preselected.getAttribute('data-tmp-option') : 'subscribe';
    }

    function selectedOption() {
      if (!sellingPlanSelect) return null;
      return sellingPlanSelect.options[sellingPlanSelect.selectedIndex] || null;
    }
    function currentPlanId() {
      return sellingPlanSelect ? sellingPlanSelect.value : '';
    }
    function currentPlanPrice() {
      var opt = selectedOption();
      return opt ? opt.getAttribute('data-plan-price') || '' : '';
    }
    function currentPlanDiscount() {
      var opt = selectedOption();
      return opt ? parseInt(opt.getAttribute('data-plan-discount'), 10) || 0 : 0;
    }

    function variantRecord() {
      if (!variants.length || !currentVariantId) return null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(currentVariantId)) return variants[i];
      }
      return null;
    }

    function priceRecord() {
      return variantPrices[currentVariantId] || null;
    }

    /* Minor units for ONE unit of what the shopper has actually selected — the
       subscription allocation price in subscribe mode, the variant price otherwise. */
    function unitCents() {
      var rec = priceRecord();
      var base = rec && typeof rec.cents === 'number' ? rec.cents : 0;
      if (!hasSellingPlans || purchaseType !== 'subscribe') return base;
      var planId = currentPlanId();
      var plans = rec && rec.plans;
      if (plans && planId && typeof plans[planId] === 'number') return plans[planId];
      return base;
    }

    /* Volume tiers are ONE-TIME ONLY: a subscription never stacks a quantity
       discount, so subscribe mode always returns 0 and pays qty × plan price. */
    function tierPercent(qty) {
      if (hasSellingPlans && purchaseType === 'subscribe') return 0;
      if (qty >= 3) return tier3Percent;
      if (qty === 2) return tier2Percent;
      return 0;
    }

    function lineTotalCents(qty) {
      return Math.round((unitCents() * qty * (100 - tierPercent(qty))) / 100);
    }

    /* Update the subscribe card's price + discount badge from the chosen plan. */
    function updateSubscribeUI() {
      if (!hasSellingPlans) return;
      var planPrice = currentPlanPrice();
      if (subPriceEl && planPrice) subPriceEl.textContent = planPrice;
      if (subBadgeEl) {
        var disc = currentPlanDiscount();
        if (disc > 0) {
          subBadgeEl.textContent = 'Save ' + disc + '%';
          subBadgeEl.hidden = false;
        } else {
          subBadgeEl.hidden = true;
        }
      }
    }

    /* Update only the price token in the ATC label — never touches availability.
       At quantity 1 the label carries no price (the price block above already says
       it); from 2 up it carries the line total for the current mode + tier. The
       sticky bar shows that same total at every quantity. Bails out when the variant
       is unknown so the server-rendered money is never blanked by a bad lookup. */
    function updateAtcPrice() {
      if (!priceRecord()) return;
      var qty = currentQuantity();
      var total = formatMoney(lineTotalCents(qty), moneyFormat, currencyCode);
      if (stickyPriceEl) stickyPriceEl.textContent = total;
      if (!atcButton || atcButton.disabled) return;
      var priceEl = atcButton.querySelector('[data-tmp-atc-price]');
      if (priceEl) priceEl.textContent = qty > 1 ? ' - ' + total : '';
    }

    function syncSellingPlanInput() {
      if (!sellingPlanInput) return;
      sellingPlanInput.value = hasSellingPlans && purchaseType === 'subscribe' ? currentPlanId() : '';
    }

    /* --- Multi-variant only: resolve + apply the selected variant --- */
    function resolveVariantFromOptions() {
      if (!optionInputs.length || !variants.length) return;
      var selected = [];
      optionInputs.forEach(function (input) {
        if (input.checked) {
          selected[parseInt(input.getAttribute('data-option-position'), 10)] = input.getAttribute('data-value');
        }
      });
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        if (opts.length !== selected.length) continue;
        var match = true;
        for (var j = 0; j < opts.length; j++) {
          if (opts[j] !== selected[j]) { match = false; break; }
        }
        if (match) {
          currentVariantId = String(variants[i].id);
          if (variantIdInput) variantIdInput.value = currentVariantId;
          return;
        }
      }
    }

    /* Reads the server-rendered money map (exact | money strings) for a variant id. */
    function updateMainPrice(price) {
      if (!price) return;
      if (priceSaleEl) priceSaleEl.textContent = price.price;
      if (priceCompareEl) {
        priceCompareEl.textContent = price.onsale ? price.compare : '';
        priceCompareEl.hidden = !price.onsale;
      }
      if (priceBadgeEl) {
        priceBadgeEl.textContent = price.onsale ? 'Save ' + price.save : '';
        priceBadgeEl.hidden = !price.onsale;
      }
    }

    /* Availability re-derived from the resolved variant ON SWITCH ONLY.
       Defaults to available when the variant is unknown — server truth stands. */
    function updateAtcAvailability(variant) {
      if (!atcButton) return;
      var available = variant ? variant.available !== false : true;
      atcButton.disabled = !available;
      if (variantIdInput) variantIdInput.disabled = !available;
      if (stickyAtcButton) {
        stickyAtcButton.disabled = !available;
        stickyAtcButton.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
      }
      if (!atcText) return;
      if (available) {
        if (!atcButton.querySelector('[data-tmp-atc-price]')) {
          atcText.innerHTML = 'ADD TO CART<span data-tmp-atc-price></span>';
        }
        updateAtcPrice();
      } else {
        atcText.textContent = 'SOLD OUT';
      }
    }

    /* Variant option changes (multi-variant products) */
    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        resolveVariantFromOptions();
        var v = variantRecord();
        var price = variantPrices[currentVariantId];
        updateGalleryImage(v);
        updateMainPrice(price);
        if (price && oneTimePriceEl) oneTimePriceEl.textContent = price.price;
        updateAtcAvailability(v);
        updateSubscribeUI();
        syncSellingPlanInput();
        updateQuantityUI();
      });
    });

    /* Purchase option (subscribe vs one-time) */
    if (purchaseOptions) {
      var options = purchaseOptions.querySelectorAll('[data-tmp-option]');

      /* Switching mode re-decides three things at once: which unit price feeds the
         totals, whether a volume tier applies at all, and whether the nudge line has
         anything to say — so it routes through updateQuantityUI, not updateAtcPrice. */
      function selectOption(type) {
        purchaseType = type;
        options.forEach(function (opt) {
          opt.classList.toggle('is-selected', opt.getAttribute('data-tmp-option') === type);
        });
        syncSellingPlanInput();
        updateQuantityUI();
      }

      options.forEach(function (opt) {
        opt.addEventListener('click', function () {
          selectOption(opt.getAttribute('data-tmp-option'));
        });
      });

      if (sellingPlanSelect) {
        sellingPlanSelect.addEventListener('change', function () {
          updateSubscribeUI();
          /* Picking a frequency IS choosing to subscribe; selectOption re-syncs the
             hidden selling_plan input and recomputes the totals off the new plan. */
          selectOption('subscribe');
        });
        sellingPlanSelect.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      }
    }

    /* --- Quantity → savings nudge + ATC / sticky totals ---
       Shopify still applies the tier discounts at cart/checkout level; the totals
       shown here are the same arithmetic surfaced early so the button states what
       the shopper is about to pay. */
    function currentQuantity() {
      var value = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return !value || value < 1 ? 1 : value;
    }

    function updateQuantityUI() {
      var qty = currentQuantity();
      var tier = String(qty >= 3 ? 3 : qty);
      nudgeLines.forEach(function (line) {
        line.classList.toggle('is-active', line.getAttribute('data-tmp-nudge-tier') === tier);
      });
      /* Tiers don't apply to subscriptions, so the line collapses entirely rather
         than reserving empty space — subscribe mode should read as intentional. */
      if (nudge) nudge.hidden = hasSellingPlans && purchaseType === 'subscribe';
      updateAtcPrice();
    }

    if (qtyInput) {
      /* quantity-selector:update bubbles from the native component on +/- and on
         blur; the input listener covers live typing before blur commits. */
      root.addEventListener('quantity-selector:update', updateQuantityUI);
      qtyInput.addEventListener('input', updateQuantityUI);
    }

    /* --- Sticky ATC bar visibility ---
       Shown only once the buy row has scrolled ABOVE the viewport. Checking the
       row's bottom against the root's top (rather than bare !isIntersecting)
       keeps the bar hidden while the shopper is still above the buy box — on
       mobile the gallery sits above it, so the row is off-screen at page load. */
    if (stickyBar && buyRow && typeof window.IntersectionObserver === 'function') {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
            var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
            stickyBar.classList.toggle('is-visible', scrolledPast);
          });
        },
        { threshold: 0 }
      );
      observer.observe(buyRow);
    }

    /* Prime prices, the nudge line + the hidden selling_plan input from the
       server-rendered default selection. Deliberately does NOT touch availability —
       the server-rendered button state is authoritative. */
    updateSubscribeUI();
    syncSellingPlanInput();
    updateQuantityUI();

    /* --- FAQ accordion --- */
    var faqList = root.querySelector('.trinsic-pdp-faq');
    if (faqList) {
      var items = faqList.querySelectorAll('.trinsic-pdp-faq__item');
      var reduceMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

      var prefersReducedMotion = function () {
        return !!(reduceMotion && reduceMotion.matches);
      };

      var FAQ_DURATION = 280;
      var FAQ_EASING = 'cubic-bezier(0.33, 0, 0.2, 1)';

      var canAnimate = function (wrap) {
        if (prefersReducedMotion()) return false;
        return typeof wrap.animate === 'function';
      };

      /* Height the panel is painting at RIGHT NOW — mid-tween included. */
      var currentHeight = function (wrap) {
        return wrap.getBoundingClientRect().height;
      };

      /* Cancelling reverts the wrap to its underlying auto height in the same
         task, so no frame is painted between one tween and the next. */
      var stopAnim = function (wrap) {
        if (wrap.trinsicFaqAnim) {
          wrap.trinsicFaqAnim.cancel();
          wrap.trinsicFaqAnim = null;
        }
      };

      /* A closed <details> keeps its content unrendered, so a CSS transition
         has no start size to run from and the panel snaps. The CSS-only cure
         (::details-content + interpolate-size + @starting-style) is
         Chromium-only and grid-template-rows interpolation needs Safari 16+, so
         the height is measured and animated in pixels instead — every engine,
         both viewports, interruptible mid-flight. No inline height is committed,
         so the panel settles back to auto height and keeps reflowing. */
      var openItem = function (details, wrap) {
        delete details.dataset.faqClosing;
        details.classList.add('is-active');

        var from = details.open ? currentHeight(wrap) : 0;
        stopAnim(wrap);
        details.open = true; /* render the content before measuring it */

        if (!canAnimate(wrap)) return;

        var to = wrap.scrollHeight;
        if (to <= from) return;

        var anim = wrap.animate(
          [
            { height: from + 'px', opacity: from ? 1 : 0 },
            { height: to + 'px', opacity: 1 },
          ],
          { duration: FAQ_DURATION, easing: FAQ_EASING }
        );
        wrap.trinsicFaqAnim = anim;
        anim.onfinish = function () {
          wrap.trinsicFaqAnim = null;
        };
      };

      var closeItem = function (details, wrap) {
        if (!details.open || details.dataset.faqClosing) return;

        details.classList.remove('is-active');

        var from = currentHeight(wrap);
        stopAnim(wrap);

        if (!canAnimate(wrap)) {
          details.open = false;
          return;
        }

        details.dataset.faqClosing = 'true';

        var anim = wrap.animate(
          [
            { height: from + 'px', opacity: 1 },
            { height: '0px', opacity: 0 },
          ],
          { duration: FAQ_DURATION, easing: FAQ_EASING }
        );
        wrap.trinsicFaqAnim = anim;
        /* onfinish only — a cancel means openItem() took over and owns the state. */
        anim.onfinish = function () {
          wrap.trinsicFaqAnim = null;
          delete details.dataset.faqClosing;
          details.open = false;
        };
      };

      items.forEach(function (details) {
        var summary = details.querySelector('summary');
        var wrap = details.querySelector('.trinsic-pdp-faq__answer-wrap');
        if (!summary || !wrap) return;

        summary.addEventListener('click', function (e) {
          /* Drive the toggle by hand — the native one flips [open] instantly
             and leaves nothing to animate. Enter/Space fire a click too. */
          e.preventDefault();

          if (details.open && !details.dataset.faqClosing) {
            closeItem(details, wrap);
            return;
          }

          items.forEach(function (other) {
            if (other === details) return;
            var otherWrap = other.querySelector('.trinsic-pdp-faq__answer-wrap');
            if (otherWrap) closeItem(other, otherWrap);
          });

          openItem(details, wrap);
        });
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[id^="TrinsicMainProduct-"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[id^="TrinsicMainProduct-"]');
    if (root) {
      root.dataset.tmpInit = '';
      initSection(root);
    }
  });
})();
